import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, STATUS_LABELS, STATUS_CLASS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { WarningCircle, WhatsappLogo, EnvelopeSimple, CheckCircle, Receipt, Plus, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { InvoicePayment } from "@/components/InvoicePayment";

const CAT_CLASS = { taxa: "status-pendente", pintura: "status-analise", limpeza: "status-assinatura", aluguel: "status-inadimplente", outros: "status-finalizado" };

export default function Delinquency() {
  const [data, setData] = useState(null);
  const [addFor, setAddFor] = useState(null);
  const [form, setForm] = useState({ category: "aluguel", description: "", amount: "" });
  const [boleto, setBoleto] = useState(null);

  const load = () => api.get("/delinquency").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const markPaid = async (c, p) => {
    try {
      await api.patch(`/contracts/${c.contract_id}/pendencias/${p.pendencia_id}`, { status: "paga" });
      toast.success("Pendência baixada"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };
  const genBoleto = async (c, p) => {
    try {
      const { data: inv } = await api.post(`/contracts/${c.contract_id}/pendencias/${p.pendencia_id}/boleto`);
      setBoleto(inv); toast.success(inv.provider === "INTER" && inv.inter_codigo ? "Boleto emitido no Banco Inter" : "Cobrança gerada"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao gerar boleto"); }
  };
  const addPend = async () => {
    if (!form.amount) { toast.error("Informe o valor"); return; }
    try {
      await api.post(`/contracts/${addFor.contract_id}/pendencias`, { ...form, amount: parseFloat(form.amount) });
      toast.success("Pendência adicionada"); setAddFor(null); setForm({ category: "aluguel", description: "", amount: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  const msg = (c) => encodeURIComponent(
    `Olá ${c.tenant_name}, aqui é da Safimob Garantidora. Identificamos pendências no contrato do imóvel ${c.property_address}:\n` +
    c.items.map((i) => `• ${data.categories[i.category]}: ${i.description} — ${fmtBRL(i.amount)}`).join("\n") +
    `\nTotal: ${fmtBRL(c.total)}.` + (c.portal_token ? `\nAcesse seu portal para pagar: ${window.location.origin}/portal/${c.portal_token}` : ""));

  if (!data) return <div className="p-10 text-slate-500">Carregando…</div>;

  return (
    <div className="p-8 max-w-[1400px]" data-testid="delinquency-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1">Cobrança</div>
          <h1 className="font-display text-4xl tracking-tight">Inadimplência</h1>
          <p className="text-slate-600 mt-2 max-w-2xl text-sm">Contratos com pendências em aberto (taxa, pintura, limpeza, aluguel) e faturas vencidas, com os dados para cobrança.</p>
        </div>
        <div className="flex gap-3">
          <Kpi label="Contratos" value={data.count} testId="del-count" />
          <Kpi label="Total em aberto" value={fmtBRL(data.total)} tone="danger" testId="del-total" />
        </div>
      </div>

      {data.items.length === 0 && (
        <div className="card-flat p-12 text-center text-slate-500" data-testid="del-empty">
          <CheckCircle size={40} weight="duotone" className="mx-auto text-emerald-600 mb-3" /> Nenhuma pendência em aberto.
        </div>
      )}

      <div className="space-y-4">
        {data.items.map((c) => (
          <div key={c.contract_id} className="card-flat p-6" data-testid={`del-card-${c.contract_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-2xl tracking-tight">{c.tenant_name}</div>
                  <span className={`status-pill ${STATUS_CLASS[c.status]}`}>{STATUS_LABELS[c.status]}</span>
                  {c.days_overdue > 0 && <span className="status-pill status-inadimplente" data-testid={`del-days-${c.contract_id}`}><span>{c.days_overdue} dias em atraso</span></span>}
                </div>
                <div className="text-xs text-slate-500 mt-1 space-x-3">
                  <span className="mono">{c.tenant_cpf}</span>
                  {c.tenant_phone && <span>{c.tenant_phone}</span>}
                  {c.tenant_email && <span>{c.tenant_email}</span>}
                </div>
                <div className="text-sm text-slate-700 mt-2">{c.property_address} · Proprietário: {c.owner_name} · Aluguel {fmtBRL(c.rent_amount)}</div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {Object.entries(c.by_category).map(([k, v]) => (
                    <span key={k} className={`status-pill ${CAT_CLASS[k]}`} data-testid={`del-cat-${c.contract_id}-${k}`}><span>{data.categories[k]}: {fmtBRL(v)}</span></span>
                  ))}
                </div>
              </div>
              <div className="text-right">
                <div className="eyebrow">Total devido</div>
                <div className="metric-value text-3xl text-red-700" data-testid={`del-card-total-${c.contract_id}`}>{fmtBRL(c.total)}</div>
                <div className="flex gap-2 mt-3 justify-end flex-wrap">
                  {c.tenant_phone && (
                    <a href={`https://wa.me/55${c.tenant_phone.replace(/\D/g, "")}?text=${msg(c)}`} target="_blank" rel="noreferrer" data-testid={`del-wa-${c.contract_id}`}>
                      <Button size="sm" className="bg-[#25D366] hover:bg-[#1ebe5d] text-white"><WhatsappLogo size={14} className="mr-1" /> Cobrar no WhatsApp</Button>
                    </a>
                  )}
                  {c.tenant_email && (
                    <a href={`mailto:${c.tenant_email}?subject=${encodeURIComponent("Pendências — Safimob Garantidora")}&body=${msg(c)}`} data-testid={`del-mail-${c.contract_id}`}>
                      <Button size="sm" variant="outline"><EnvelopeSimple size={14} className="mr-1" /> E-mail</Button>
                    </a>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setAddFor(c)} data-testid={`del-add-${c.contract_id}`}><Plus size={14} className="mr-1" /> Pendência</Button>
                </div>
              </div>
            </div>

            <table className="w-full text-sm mt-5">
              <thead className="text-slate-500 eyebrow">
                <tr><th className="text-left py-2">Categoria</th><th className="text-left py-2">Descrição</th><th className="text-left py-2">Desde</th><th className="text-right py-2">Valor</th><th className="text-right py-2"></th></tr>
              </thead>
              <tbody>
                {c.items.map((p, i) => (
                  <tr key={p.pendencia_id || p.invoice_id || i} className="border-t border-[var(--line)]" data-testid={`del-item-${p.pendencia_id || p.invoice_id}`}>
                    <td className="py-2"><span className={`status-pill ${CAT_CLASS[p.category]}`}><span>{data.categories[p.category]}</span></span></td>
                    <td className="py-2 text-slate-700">{p.description}</td>
                    <td className="py-2 text-slate-500">{fmtDate(p.due_date || p.created_at)}</td>
                    <td className="py-2 mono text-right">{fmtBRL(p.amount)}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {p.pendencia_id && !p.invoice_id && (
                        <Button size="sm" variant="ghost" onClick={() => genBoleto(c, p)} data-testid={`del-boleto-${p.pendencia_id}`}><Receipt size={14} className="mr-1" /> Gerar boleto</Button>
                      )}
                      {p.pendencia_id && (
                        <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => markPaid(c, p)} data-testid={`del-paid-${p.pendencia_id}`}><CheckCircle size={14} className="mr-1" /> Baixar</Button>
                      )}
                      {!p.pendencia_id && <span className="text-xs text-slate-400">fatura vencida</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <Dialog open={!!addFor} onOpenChange={() => setAddFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nova pendência</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="pend-category"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(data.categories).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500 mb-1.5 block">Descrição</Label><Input data-testid="pend-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-500 mb-1.5 block">Valor (R$)</Label><Input data-testid="pend-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddFor(null)}>Cancelar</Button>
            <Button className="btn-primary" onClick={addPend} data-testid="pend-save">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!boleto} onOpenChange={() => setBoleto(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Cobrança gerada</DialogTitle></DialogHeader>
          {boleto && <><div className="metric-value text-3xl mb-2">{fmtBRL(boleto.amount)}</div><InvoicePayment invoice={boleto} /></>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, tone, testId }) {
  return (
    <div className="card-flat px-5 py-3 min-w-[140px]">
      <div className="eyebrow">{label}</div>
      <div className={`metric-value text-2xl mt-1 ${tone === "danger" ? "text-red-700" : ""}`} data-testid={testId}>{value}</div>
    </div>
  );
}
